import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Building2, Phone, FileText, Palette, Save, Globe, Scale, FolderKanban, Copy } from 'lucide-react';
import { useCompanyProfile } from '@/hooks/useCompanyProfile';
import { toast } from '@/hooks/use-toast';
import { LogoUpload } from '@/components/settings/LogoUpload';
import { ColorPicker } from '@/components/settings/ColorPicker';
import { TaxSettingsCard } from '@/components/settings/TaxSettingsCard';
import { COMMON_TIMEZONES } from '@/data/timezones';
import { SUPPORTED_COUNTRIES, resolveCompanyLocale, isSupportedCountry, canonicalCountryName } from '@/utils/locale';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useEffect } from 'react';

const companyProfileSchema = z.object({
  // Company Details
  name: z.string().min(1, 'Company name is required').max(255),
  legal_name: z.string().max(255).optional(),
  // Shown to customers on documents and in the technician app when set (falls back to name).
  trading_name: z.string().max(255).optional(),
  // Secondary identifier (ACN / GST No. / VAT No. …); DB column is VARCHAR(9) after spaces are stripped.
  acn: z.string().max(12).optional(),
  // Primary business identifier (ABN / EIN / Company No. / NZBN …). The DB column is
  // VARCHAR(11) after spaces are stripped on save; the AU-specific 11-digit rule is
  // enforced at object level below because it depends on the chosen country.
  abn: z.string().max(20).optional(),
  website: z.string().max(500).optional().or(z.literal('')),
  business_hours: z.string().max(500).optional(),
  timezone: z.string().min(1, 'Timezone is required'),
  
  // Contact Information
  phone: z.string().max(20).optional(),
  email: z.string().email().max(255).optional().or(z.literal('')),
  street_address: z.string().max(500).optional(),
  suburb: z.string().max(100).optional(),
  state: z.string().max(50).optional(),
  postcode: z.string().max(10).optional(),
  country: z.string().max(100).optional(),
  
  // Invoice & Quote Settings
  invoice_prefix: z.string().max(20).optional(),
  estimate_prefix: z.string().max(20).optional(),
  estimate_valid_days: z.number().int().min(1, 'At least 1 day').max(365, 'At most 365 days'),
  estimate_deposit_percent: z.number().min(0, 'Cannot be negative').max(100, 'At most 100%'),
  invoice_terms: z.string().max(1000).optional(),
  warranty_text: z.string().max(1000).optional(),
  payment_instructions: z.string().max(1000).optional(),
  bank_account_name: z.string().max(255).optional(),
  bsb: z.string().max(10).optional(),
  account_number: z.string().max(20).optional(),
  card_processing_fee_rate: z.number().min(0).max(10).optional(),
  parts_markup_pct: z.number().min(0).max(1000).optional(),
  default_part_source: z.enum(['company_stock', 'tech_personal']),

  // Tax Settings
  tax_enabled: z.boolean(),
  tax_rate: z.number().min(0).max(100),
  tax_label: z.string().min(1, 'Tax label is required').max(20),
  tax_inclusive: z.boolean(),

  // Branding
  logo_url: z.string().max(500).optional(),
  primary_color: z.string().max(7).optional(),
  secondary_color: z.string().max(7).optional(),
  email_signature: z.string().max(2000).optional(),
  
  // Social Media
  facebook_url: z.string().url().optional().or(z.literal('')),
  instagram_url: z.string().url().optional().or(z.literal('')),
  linkedin_url: z.string().url().optional().or(z.literal('')),
  
  // Legal Documents
  terms_and_conditions_url: z.string().url().optional().or(z.literal('')),
  privacy_policy_url: z.string().url().optional().or(z.literal('')),
}).superRefine((values, ctx) => {
  const compact = (values.abn ?? '').replace(/\s/g, '');
  if (compact.length > 11) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['abn'], message: 'Must be at most 11 characters' });
  }
  if (resolveCompanyLocale(values.country).countryCode === 'AU' && compact && !/^\d{11}$/.test(compact)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['abn'], message: 'ABN must be 11 digits' });
  }
  const acnCompact = (values.acn ?? '').replace(/\s/g, '');
  if (acnCompact.length > 9) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['acn'], message: 'Must be at most 9 characters' });
  }
  if (resolveCompanyLocale(values.country).countryCode === 'AU' && acnCompact && !/^\d{9}$/.test(acnCompact)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['acn'], message: 'ACN must be 9 digits' });
  }
});

type CompanyProfileFormValues = z.infer<typeof companyProfileSchema>;

export function CompanyProfile() {
  const { data: company, isLoading, update, isUpdating } = useCompanyProfile();

  const form = useForm<CompanyProfileFormValues>({
    resolver: zodResolver(companyProfileSchema),
    defaultValues: {
      name: '',
      legal_name: '',
      trading_name: '',
      abn: '',
      acn: '',
      website: '',
      business_hours: 'Mon-Fri: 8am-5pm, Sat: 9am-1pm',
      timezone: 'Australia/Sydney',
      phone: '',
      email: '',
      street_address: '',
      suburb: '',
      state: '',
      postcode: '',
      country: 'Australia',
      invoice_prefix: 'INV',
      estimate_prefix: 'EST',
      estimate_valid_days: 14,
      estimate_deposit_percent: 0,
      invoice_terms: 'Payment due within 14 days',
      warranty_text: '',
      payment_instructions: '',
      bank_account_name: '',
      bsb: '',
      account_number: '',
      card_processing_fee_rate: 1.5,
      parts_markup_pct: 0,
      default_part_source: 'company_stock',
      tax_enabled: true,
      tax_rate: 10,
      tax_label: 'GST',
      tax_inclusive: true,
      logo_url: '',
      primary_color: '#1e3a8a',
      secondary_color: '#3b82f6',
      email_signature: '',
      facebook_url: '',
      instagram_url: '',
      linkedin_url: '',
      terms_and_conditions_url: '',
      privacy_policy_url: '',
    },
  });

  useEffect(() => {
    if (company) {
      form.reset({
        name: company.name || '',
        legal_name: company.legal_name || '',
        trading_name: company.trading_name || '',
        abn: company.abn || '',
        acn: company.acn || '',
        website: company.website || '',
        business_hours: company.business_hours || 'Mon-Fri: 8am-5pm, Sat: 9am-1pm',
        timezone: company.timezone || 'Australia/Sydney',
        phone: company.phone || '',
        email: company.email || '',
        street_address: company.street_address || '',
        suburb: company.suburb || '',
        state: company.state || '',
        postcode: company.postcode || '',
        country: company.country || 'Australia',
        invoice_prefix: company.invoice_prefix || 'INV',
        estimate_prefix: company.estimate_prefix || 'EST',
        estimate_valid_days: company.estimate_valid_days ?? 14,
        estimate_deposit_percent: Number(company.estimate_deposit_percent ?? 0),
        invoice_terms: company.invoice_terms || 'Payment due within 14 days',
        warranty_text: company.warranty_text || '',
        payment_instructions: company.payment_instructions || '',
        bank_account_name: company.bank_account_name || '',
        bsb: company.bsb || '',
        account_number: company.account_number || '',
        card_processing_fee_rate: (company as any).card_processing_fee_rate ?? 1.5,
        parts_markup_pct: company.parts_markup_pct ?? 0,
        default_part_source: company.default_part_source === 'tech_personal' ? 'tech_personal' : 'company_stock',
        tax_enabled: company.tax_enabled ?? true,
        tax_rate: company.tax_rate ?? 10,
        tax_label: company.tax_label ?? 'GST',
        tax_inclusive: company.tax_inclusive ?? true,
        logo_url: company.logo_url || '',
        primary_color: company.primary_color || '#1e3a8a',
        secondary_color: company.secondary_color || '#3b82f6',
        email_signature: company.email_signature || '',
        facebook_url: company.facebook_url || '',
        instagram_url: company.instagram_url || '',
        linkedin_url: company.linkedin_url || '',
        terms_and_conditions_url: (company as any).terms_and_conditions_url || '',
        privacy_policy_url: (company as any).privacy_policy_url || '',
      });
    }
  }, [company, form]);

  // Labels, placeholders and validation follow the selected country.
  const watchedCountry = form.watch('country');
  const profileLocale = resolveCompanyLocale(watchedCountry);
  // Changing country moves the tax defaults with it, otherwise a UK tenant keeps
  // printing "GST (10%)" on GBP invoices. Only overwrite when the current values are
  // still the previous country's defaults, so a hand-tuned rate is never clobbered.
  const handleCountryChange = (nextName: string) => {
    const prev = resolveCompanyLocale(form.getValues('country'));
    const next = resolveCompanyLocale(nextName);
    form.setValue('country', nextName, { shouldDirty: true });
    if (next.countryCode === prev.countryCode) return;
    const untouched =
      form.getValues('tax_label') === prev.taxDefaults.label &&
      Number(form.getValues('tax_rate')) === prev.taxDefaults.rate;
    if (untouched) {
      form.setValue('tax_label', next.taxDefaults.label, { shouldDirty: true });
      form.setValue('tax_rate', next.taxDefaults.rate, { shouldDirty: true });
      form.setValue('tax_inclusive', next.taxDefaults.inclusive, { shouldDirty: true });
      form.setValue('tax_enabled', next.taxDefaults.enabled, { shouldDirty: true });
      toast({
        title: `Tax settings switched to ${next.name}`,
        description: `${next.taxDefaults.label} ${next.taxDefaults.rate}% — adjust below if yours differ.`,
      });
    }
  };


  const onSubmit = (values: CompanyProfileFormValues) => {
    update({
      ...values,
      abn: values.abn ? values.abn.replace(/\s/g, '') : values.abn,
      acn: values.acn ? values.acn.replace(/\s/g, '') : values.acn,
      trading_name: values.trading_name?.trim() || null,
    });
  };

  const handleCopyCode = async () => {
    if (!company?.public_key) return;
    try {
      await navigator.clipboard.writeText(company.public_key);
      toast({ title: 'Company code copied' });
    } catch {
      toast({
        title: 'Copy failed',
        description: 'Select the code and copy it manually.',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return <div className="text-center py-8 text-muted-foreground">Loading company profile...</div>;
  }

  return (
    <>
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {/* Company Details */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-primary" />
              <CardTitle>Company Details</CardTitle>
            </div>
            <CardDescription>
              Basic information about your company
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            {/* Company Code Display */}
            <div className="md:col-span-2 space-y-2">
              <FormLabel>Company Code</FormLabel>
              <div className="flex items-center gap-4 rounded-lg bg-primary/10 p-4">
                <div className="text-[26px] font-semibold tracking-[-0.02em] text-primary tabular-nums">
                  {company?.public_key || 'Loading...'}
                </div>
                <p className="flex-1 text-[13px] text-muted-foreground">
                  Your unique 6-digit company identifier. Use this code when inviting users or setting up integrations.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  onClick={handleCopyCode}
                  disabled={!company?.public_key}
                >
                  <Copy className="h-3.5 w-3.5" />
                  Copy
                </Button>
              </div>
            </div>
            
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="md:col-span-2">
                  <FormLabel>Company Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="ABC Plumbing Services" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="legal_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Legal Name</FormLabel>
                  <FormControl>
                    <Input placeholder="ABC Plumbing Pty Ltd" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="trading_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Trading Name</FormLabel>
                  <FormControl>
                    <Input placeholder="ABC Plumbing" {...field} />
                  </FormControl>
                  <FormDescription>The name customers see on quotes, invoices, emails and in the technician app. Leave blank to use the company name.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="abn"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{profileLocale.taxIdLabels.abn}</FormLabel>
                  <FormControl>
                    <Input placeholder={profileLocale.taxIdPlaceholder} maxLength={20} {...field} />
                  </FormControl>
                  <FormDescription>Printed on quotes and invoices (e.g. {profileLocale.taxIdPlaceholder})</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="acn"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{profileLocale.taxIdLabels.acn}</FormLabel>
                  <FormControl>
                    <Input placeholder={profileLocale.countryCode === 'AU' ? '123 456 789' : ''} maxLength={12} {...field} />
                  </FormControl>
                  <FormDescription>Optional second identifier printed next to the {profileLocale.taxIdLabels.abn}.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="website"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Website</FormLabel>
                  <FormControl>
                    <Input type="text" placeholder="https://example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="business_hours"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Business Hours</FormLabel>
                  <FormControl>
                    <Input placeholder="Mon-Fri: 8am-5pm" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="timezone"
              render={({ field }) => (
                <FormItem className="md:col-span-2">
                  <FormLabel className="flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    System Timezone
                  </FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select timezone" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {COMMON_TIMEZONES.map((tz) => (
                        <SelectItem key={tz.value} value={tz.value}>
                          {tz.label} ({tz.offset})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    All dates and times throughout the system will be displayed in this timezone.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-primary" />
              <CardTitle>Contact Information</CardTitle>
            </div>
            <CardDescription>
              How customers can reach you
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Office Phone</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder={profileLocale.phonePlaceholder} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email Address</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="info@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="street_address"
              render={({ field }) => (
                <FormItem className="md:col-span-2">
                  <FormLabel>Street Address</FormLabel>
                  <FormControl>
                    <Input placeholder="123 Main Street" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="suburb"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{profileLocale.addressLabels.locality}</FormLabel>
                  <FormControl>
                    <Input placeholder={profileLocale.addressPlaceholders.locality} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="state"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{profileLocale.addressLabels.region}</FormLabel>
                  <FormControl>
                    <Input placeholder={profileLocale.addressPlaceholders.region} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="postcode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{profileLocale.addressLabels.postcode}</FormLabel>
                  <FormControl>
                    <Input placeholder={profileLocale.postcode.placeholder} maxLength={profileLocale.postcode.maxLength} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Country</FormLabel>
                  <Select
                    onValueChange={handleCountryChange}
                    value={isSupportedCountry(field.value) ? canonicalCountryName(field.value) : ''}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {SUPPORTED_COUNTRIES.map((c) => (
                        <SelectItem key={c.countryCode} value={c.name}>
                          {c.name} ({c.currency})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Drives currency, tax labels, address format, postcodes and address lookup.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Invoice & Estimate Settings */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              <CardTitle>Invoice & Estimate Settings</CardTitle>
            </div>
            <CardDescription>
              Configure how invoices and estimates are generated
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <FormField
              control={form.control}
              name="invoice_prefix"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Invoice Prefix</FormLabel>
                  <FormControl>
                    <Input placeholder="INV" {...field} />
                  </FormControl>
                  <FormDescription>e.g., INV-0001</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="estimate_prefix"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Estimate Prefix</FormLabel>
                  <FormControl>
                    <Input placeholder="EST" {...field} />
                  </FormControl>
                  <FormDescription>e.g., EST-0001</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="estimate_valid_days"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Quote validity (days)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={1}
                      max={365}
                      step={1}
                      value={field.value ?? 14}
                      onChange={(e) => field.onChange(e.target.value === '' ? 14 : parseInt(e.target.value, 10))}
                      onBlur={field.onBlur}
                      name={field.name}
                      ref={field.ref}
                    />
                  </FormControl>
                  <FormDescription>New quotes expire this many days after they are issued, from the office and from the technician app.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="estimate_deposit_percent"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Deposit on acceptance (%)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      step={1}
                      value={field.value ?? 0}
                      onChange={(e) => field.onChange(e.target.value === '' ? 0 : parseFloat(e.target.value))}
                      onBlur={field.onBlur}
                      name={field.name}
                      ref={field.ref}
                    />
                  </FormControl>
                  <FormDescription>
                    When a customer accepts a quote online they are asked to pay this share by card (Square must be connected). 0 = no deposit. Applies to new quotes; you can change it on each quote before it is accepted.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="invoice_terms"
              render={({ field }) => (
                <FormItem className="md:col-span-2">
                  <FormLabel>Invoice Terms</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Payment due within 14 days"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="warranty_text"
              render={({ field }) => (
                <FormItem className="md:col-span-2">
                  <FormLabel>Warranty (shown on invoices)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="e.g. All workmanship is covered by a 7-year warranty. Register your warranty by scanning the QR code on your service sticker."
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>Leave blank to omit the warranty section from invoices.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="payment_instructions"
              render={({ field }) => (
                <FormItem className="md:col-span-2">
                  <FormLabel>Payment Instructions</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Please pay by bank transfer or credit card"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="bank_account_name"
              render={({ field }) => (
                <FormItem className="md:col-span-2">
                  <FormLabel>Bank Account Name</FormLabel>
                  <FormControl>
                    <Input placeholder="ABC Plumbing Pty Ltd" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="bsb"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{profileLocale.bankLabels.bsb}</FormLabel>
                  <FormControl>
                    <Input placeholder={profileLocale.bsbPlaceholder} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="account_number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account Number</FormLabel>
                  <FormControl>
                    <Input placeholder="12345678" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="card_processing_fee_rate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Card Processing Fee Rate (%)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="10"
                      placeholder="1.5"
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
                    />
                  </FormControl>
                  <FormDescription>
                    Fee percentage deducted from card/EFTPOS payments (0-10%)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="parts_markup_pct"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Parts Markup (%)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="1000"
                      placeholder="0"
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
                    />
                  </FormControl>
                  <FormDescription>
                    Customer price for parts = your cost + this %. Override per part in the Parts Catalog or on the job.
                    The cost price never appears on estimates or invoices.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="default_part_source"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Who pays for new parts by default</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="company_stock">Company stock</SelectItem>
                      <SelectItem value="tech_personal">Technician (reimbursed)</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Preselected when a part is added to a job, here or in the Steddy app. It can still be changed per part.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Tax Settings */}
        <TaxSettingsCard form={form} country={form.watch('country')} />

        {/* Branding & Communication */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Palette className="h-4 w-4 text-primary" />
              <CardTitle>Branding & Communication</CardTitle>
            </div>
            <CardDescription>
              Customize your brand appearance and social media links
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <LogoUpload
              currentLogoUrl={form.watch('logo_url')}
              onUploadSuccess={(url) => {
                // The upload toast says "uploaded": persist it now rather than
                // waiting for Save Changes, which testers reasonably skipped.
                form.setValue('logo_url', url, { shouldDirty: false });
                update({ logo_url: url });
              }}
            />
            
            <Separator />
            
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="primary_color"
                render={({ field }) => (
                  <FormItem>
                    <ColorPicker
                      label="Primary Color"
                      value={field.value}
                      onChange={field.onChange}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="secondary_color"
                render={({ field }) => (
                  <FormItem>
                    <ColorPicker
                      label="Secondary Color"
                      value={field.value}
                      onChange={field.onChange}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="email_signature"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email Signature</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Best regards,&#10;ABC Plumbing Team&#10;Ph: 02 1234 5678"
                      rows={4}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Added under emails your team writes from the CRM. Leave blank to sign off with the sender's name and your trading name.
                    Every customer email also ends with your trading name, phone, website, address and business number from this page.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Separator />

            <div className="space-y-4">
              <h4 className="font-medium">Social Media Links</h4>
              <FormField
                control={form.control}
                name="facebook_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Facebook</FormLabel>
                    <FormControl>
                      <Input type="text" placeholder="https://facebook.com/yourpage" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="instagram_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Instagram</FormLabel>
                    <FormControl>
                      <Input type="text" placeholder="https://instagram.com/yourpage" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="linkedin_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>LinkedIn</FormLabel>
                    <FormControl>
                      <Input type="text" placeholder="https://linkedin.com/company/yourcompany" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Legal Documents */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Scale className="h-4 w-4 text-primary" />
              <CardTitle>Legal Documents</CardTitle>
            </div>
            <CardDescription>
              Links to your Terms & Conditions and Privacy Policy for customer acceptance
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <FormField
              control={form.control}
              name="terms_and_conditions_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Terms & Conditions URL</FormLabel>
                  <FormControl>
                    <Input 
                      type="text" 
                      placeholder="https://yoursite.com/terms" 
                      {...field} 
                    />
                  </FormControl>
                  <FormDescription>
                    Customers must agree to these terms when signing estimates
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="privacy_policy_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Privacy Policy URL</FormLabel>
                  <FormControl>
                    <Input 
                      type="text" 
                      placeholder="https://yoursite.com/privacy" 
                      {...field} 
                    />
                  </FormControl>
                  <FormDescription>
                    Required for GDPR/privacy compliance
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Sticky footer actions */}
        <div className="sticky bottom-0 z-10 flex items-center justify-end gap-3 border-t bg-background/95 py-4 backdrop-blur">
          <Button
            type="button"
            variant="outline"
            onClick={() => form.reset()}
            disabled={isUpdating}
          >
            Discard
          </Button>
          <Button type="submit" disabled={isUpdating}>
            <Save className="mr-2 h-4 w-4" />
            {isUpdating ? 'Saving...' : 'Save changes'}
          </Button>
        </div>
      </form>
    </Form>

    {/* Features Card — saved immediately on toggle, outside main form */}
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <FolderKanban className="h-4 w-4 text-primary" />
          <CardTitle>Features</CardTitle>
        </div>
        <CardDescription>
          Enable or disable optional modules for your team
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div className="space-y-0.5">
            <Label className="text-base font-medium">Projects & Installations</Label>
            <p className="text-sm text-muted-foreground">
              Enables a Kanban board to track multi-job installation projects through custom pipeline stages.
            </p>
          </div>
          <Switch
            checked={(company as any)?.enable_extended_projects ?? false}
            onCheckedChange={(checked) => update({ enable_extended_projects: checked } as any)}
            disabled={isUpdating || isLoading}
          />
        </div>
      </CardContent>
    </Card>
    </>
  );
}
